import {validateIssueBody, validateIssueTitle} from '@github-ui/entity-validators'
import {fetchTemplate} from '@github-ui/issue-create/fetch-template'
import {instanceOfIssueFormData, type IssueCreatePayload} from '@github-ui/issue-create/Model'
import {getSelectedTemplate} from '@github-ui/issue-create/use-handle-template-change'
import {IssueFormElements} from '@github-ui/issue-form/IssueFormElements'
import type {IssueFormRef} from '@github-ui/issue-form/Types'
import {fetchRepository, type RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {PartialEntry} from '@github-ui/react-core/partial-entry'
import {createReactRootErrorHandlers} from '@github-ui/react-core/react-root-error-handlers'
import {RelayEnvironmentProvider} from '@github-ui/relay-environment'
import {type RefObject, useEffect, useMemo, useRef, useState} from 'react'
import {flushSync} from 'react-dom'
import {createRoot} from 'react-dom/client'
import {useRelayEnvironment} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {DraftIssue, DraftOrExistingIssue} from '../content-preview-types'
import {type TreeNode, visit} from './use-draft-issue-tree-map'
import {fetchIssue} from './use-issue-query'

export type ValidationResult = {
  [tag: string]: {
    errorMessages: string[]
  }
}

const MAX_ISSUE_WRITES = 60 // Must match MAX_ISSUE_WRITES in copilot_issues/bulk_create_job_status.rb
const MAX_HEIGHT = 7 // Height of the tree *not* including the parent. Must match sub-issues/utils/use-alert.ts and hierarchy/app/models/sub_issue.rb.
const MAX_SUBISSUES_BREADTH = 100 // Maximum number of sub-issues an issue can have. Must match MAXIMUM_BREADTH in hierarchy/app/models/sub_issue.rb.

// Use TREE_ERROR_KEY inside a function or hook to avoid accessing 'crypto' at module scope
export const TREE_ERROR_KEY = getTreeErrorKey()
export const MAX_ISSUE_COUNTS_ERROR = `You cannot create more than ${MAX_ISSUE_WRITES} new issues at a time. Please remove some draft issues from the tree and try again.`
export const MAX_HEIGHT_ERROR = `You cannot add more than ${MAX_HEIGHT} levels of sub-issues. Please remove some issues from the tree and try again.`

export function useBulkIssueValidation(issueNode: TreeNode<DraftOrExistingIssue>) {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<ValidationResult>({})

  const environment = useRelayEnvironment()
  const issueFormRef = useRef<IssueFormRef | null>(null)

  useEffect(() => {
    async function doValidation() {
      setLoading(true)
      setResult(await validateIssues(environment, issueFormRef, issueNode))
      setLoading(false)
    }

    void doValidation()
  }, [environment, issueNode])

  return {
    loading,
    result,
  }
}

// Returns true if the tree height exceeds MAX_HEIGHT (not including the root node)
// e.g. a tree with only a root node has height 0, a tree with root and children has height 1, etc.
// Optimized to return early when any branch exceeds MAX_HEIGHT
function isTreeHeightExceeded(node: TreeNode<DraftOrExistingIssue>, currentDepth = 0): boolean {
  // If we've already exceeded the maximum height, return true immediately
  if (currentDepth > MAX_HEIGHT) {
    return true
  }

  if (!node.children || node.children.length === 0) {
    return false
  }

  // Check each child branch - return true immediately if any exceeds the limit
  for (const child of node.children) {
    if (isTreeHeightExceeded(child, currentDepth + 1)) {
      return true
    }
  }

  return false
}

function getTreeErrorKey() {
  return crypto.randomUUID()
}

async function validateIssues(
  environment: Environment,
  issueFormRef: RefObject<IssueFormRef | null>,
  issueNode: TreeNode<DraftOrExistingIssue>,
): Promise<ValidationResult> {
  const result: ValidationResult = {}

  const drafts = new Map<string, TreeNode<DraftOrExistingIssue>>()
  visit(issueNode, node => drafts.set(node.item.tag, node))

  // Check for total number of new issues and tree height
  if (drafts.size > MAX_ISSUE_WRITES) {
    result[TREE_ERROR_KEY] = {
      errorMessages: [MAX_ISSUE_COUNTS_ERROR],
    }
    return result
  }

  if (isTreeHeightExceeded(issueNode)) {
    result[TREE_ERROR_KEY] = {
      errorMessages: [MAX_HEIGHT_ERROR],
    }
    return result
  }

  for (const [tag, node] of drafts) {
    const draft = node.item
    result[tag] = {errorMessages: []}

    if (draft.type === 'new-issue') {
      // Sometimes Copilot returns issues without a repository.
      // The UX can correct for this on render, but the user may not be looking at every sub-issue.
      // If this issue doesn't have a repository, default to the same repository as its parent issue.
      if (!draft.repository && draft.parentTag) {
        draft.repository = drafts.get(draft.parentTag)?.item?.repository
      }

      // If it still doesn't have a repository, fail validation
      if (!draft.repository) {
        result[tag].errorMessages.push('Repository is required')
        continue
      }
    }

    const [owner, repo] = draft.repository?.split('/') || []
    if (!owner || !repo) {
      result[tag].errorMessages.push('Repository must be in the format "owner/repo"')
      continue
    }

    const repository = await fetchRepository(owner, repo)
    if (!repository) {
      result[tag].errorMessages.push(`Repository not found`)
      continue
    }

    if (repository.isArchived) {
      result[tag].errorMessages.push(`Repository is archived`)
      continue
    }

    if (!repository.hasIssuesEnabled) {
      result[tag].errorMessages.push(`Repository does not have issues enabled`)
      continue
    }

    if (repository.viewerInteractionLimitReasonHTML) {
      result[tag].errorMessages.push(repository.viewerInteractionLimitReasonHTML)
      continue
    }

    // If this issue has sub-issues, the user must have permission to create subissues in this repository
    if (
      node.children.length > 0 &&
      (repository.isLocked ||
        (repository.viewerCanPush === false && repository.viewerIssueCreationPermissions?.triageable === false))
    ) {
      result[tag].errorMessages.push('You do not have permission to create subissues in this repository')
      continue
    }

    if (draft.type === 'existing-issue') {
      if (!draft.number) {
        result[tag].errorMessages.push('Issue number is required')
        continue
      }

      // Validate that existing issues actually... exist
      const existingIssue = await fetchIssue(environment, owner, repo, draft.number)
      if (!existingIssue) {
        result[tag].errorMessages.push(`Issue not found`)
        continue
      }

      // Check if adding new sub-issues would exceed the maximum breadth
      if (node.children.length > 0) {
        const existingSubIssueCount = existingIssue.subIssues?.totalCount ?? 0
        const newSubIssueCount = node.children.length
        if (existingSubIssueCount + newSubIssueCount > MAX_SUBISSUES_BREADTH) {
          result[tag].errorMessages.push(
            `This issue already has ${existingSubIssueCount} sub-issue${
              existingSubIssueCount === 1 ? '' : 's'
            }. Adding ${newSubIssueCount} more would exceed the maximum of ${MAX_SUBISSUES_BREADTH} sub-issues.`,
          )
          continue
        }
      }

      // Everything past this point only pertains to creating new issues
      continue
    }

    // Copilot will sometimes return the template with full path, rather than just the filename
    const templateFilename = draft.template?.split('/').findLast(Boolean)
    const templateRepository = templateFilename ? await fetchTemplate(owner, repo, templateFilename) : null
    const template = templateRepository ? getSelectedTemplate(templateRepository) : null
    if (draft.template && !template) {
      result[tag].errorMessages.push(`Issue template not found`)
      continue
    }

    if (!repository.isBlankIssuesEnabled && !template) {
      result[tag].errorMessages.push('Issue template is required')
      continue
    }

    const titleValidation = validateIssueTitle(draft.name)
    if (!titleValidation.isValid) {
      result[tag].errorMessages.push(titleValidation.errorMessage || 'Issue title is invalid')
    }

    if (template && instanceOfIssueFormData(template.data)) {
      // TODO do something about incomplete template data?

      // Clear the ref before rendering the form
      issueFormRef.current = null

      const container = document.createElement('div')
      const errorHandlers = createReactRootErrorHandlers({appName: 'copilot-bulk-issue-validation'})
      const root = createRoot(container, errorHandlers)
      // Root#render is async by nature. We need to force the render in order to process and validate form fields.
      // eslint-disable-next-line @eslint-react/dom-no-flush-sync
      flushSync(() => {
        root.render(
          <PartialEntry partialName="copilot-creates-issues-bulk-validation">
            <RelayEnvironmentProvider environment={environment}>
              <IssueFormWrapper
                repository={repository}
                draftIssue={draft}
                template={template}
                outputRef={issueFormRef}
              />
            </RelayEnvironmentProvider>
          </PartialEntry>,
        )
      })

      if (!issueFormRef.current) {
        throw new Error('IssueFormRef is not set')
      }

      // Compiler doesn't recognize that the ref.current is set after rendering
      // We just checked for null/undefined above, so this cast is safe
      const issueForm = issueFormRef.current as IssueFormRef

      try {
        const invalidInputs = issueForm.getInvalidInputs()
        if (invalidInputs.length > 0) {
          // Invalid inputs do not have any label to identify the field in error
          result[tag].errorMessages.push(`Invalid form input`)
          continue
        }

        // If there were no invalid inputs, we can set the body
        draft.body = issueForm.markdown()
      } finally {
        issueForm.clearSessionStorage()
        root.unmount()
        container.remove()
      }
    }

    const bodyValidation = validateIssueBody(draft.body ?? '')
    if (!bodyValidation.isValid) {
      result[tag].errorMessages.push(bodyValidation.errorMessage || 'Issue body is invalid')
    }
  }

  return result
}

function IssueFormWrapper({
  repository,
  draftIssue,
  template,
  outputRef,
}: {
  repository: RepositoryPickerRepository
  draftIssue: DraftIssue
  template: IssueCreatePayload
  outputRef: RefObject<IssueFormRef | null>
}) {
  const subject = useMemo(() => {
    return {
      type: 'issue' as const,
      repository: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        databaseId: repository.databaseId!,
        nwo: `${repository.owner.login}/${repository.name}`,
        slashCommandsEnabled: repository.slashCommandsEnabled,
      },
    }
  }, [repository])

  if (!instanceOfIssueFormData(template.data)) {
    return
  }

  return (
    <IssueFormElements
      outputRef={outputRef}
      graphqlElements={template.data.elements}
      subject={subject}
      sessionStorageKey={`copilot-creates-issues:${draftIssue.tag}`}
      defaultValuesById={draftIssue.defaultValuesById}
    />
  )
}
